import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DicesIcon, MusicIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, LightbulbIcon, PencilRulerIcon, ShuffleIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/16e101e2-d128-446d-88a2-fefcb60ddae2/1792287384296_salsa-bachata-ready-mockup-box-6-volumes-b8fa25d0.webp';
export const heroGuarantees = ['Instant digital download', 'One-time payment', 'No subscription', '7-day satisfaction guarantee'];
export const stats = [{
  value: '240',
  label: 'Pages in the main volumes'
}, {
  value: '6',
  label: 'Digital salsa & bachata volumes'
}, {
  value: '5',
  label: 'Bonuses included'
}, {
  value: '1',
  label: 'Complete teaching system'
}];
export const differenceStats = [{
  value: 'Six teaching areas',
  label: 'From salsa and bachata foundations to class planning and assessment'
}, {
  value: '6 digital volumes',
  label: 'Salsa basics, bachata basics, partnerwork, musicality, turn patterns, and class planning'
}, {
  value: 'Printable PDFs',
  label: 'Use the resources digitally or print what you need for class'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Your Sunday evenings disappear',
  text: 'Hours at your computer planning next week’s dance classes — and you’re still not finished.'
}, {
  title: 'Hours spent searching',
  text: 'Drills and routines from different videos and websites are too basic, too advanced, or need adapting before you can use them.'
}, {
  title: 'Every activity starts from scratch',
  text: 'Counts, cues, practice sheets, and reflection questions — what should take minutes becomes another lengthy task.'
}, {
  title: 'It takes time to turn ideas into practical classes',
  text: 'Timing, footwork, frame, lead-and-follow, and turn patterns all need clear instructions and a structure your students can follow.'
}, {
  title: 'Assessment adds more work',
  text: 'Creating progress checks, recording observations, and tracking students’ progress means more preparation after class.'
}, {
  title: 'Stress before your next class',
  text: 'When time runs out, you find yourself pulling drills and combinations together at the last minute.'
}];
export const afterItems = ['An organised salsa & bachata teaching bundle for the whole year', 'Class structures that are easy to follow', 'Ready-to-use timing, footwork, and partnerwork drills', 'Practice sheets you can print straight away', 'Practical musicality, styling, and connection activities', 'Exercises for practice and skill development', 'Class activities that bring variety to your classes', 'Assessment and reflection tools', 'Planning support for your dance year'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Your Sundays are spent planning',
  text: 'Do you spend part of your weekend creating salsa and bachata classes for the week ahead?'
}, {
  icon: FolderSearchIcon,
  title: 'You don’t know which activity to choose',
  text: 'Are you constantly searching for drills that suit your students’ level, experience, and confidence?'
}, {
  icon: LightbulbIcon,
  title: 'You want clearer teaching instructions',
  text: 'Do you need practical ways to explain timing, footwork, frame, lead-and-follow, and musicality?'
}, {
  icon: PencilRulerIcon,
  title: 'You want to stop creating everything from zero',
  text: 'Do you wish your class plans, drills, and activities were already prepared?'
}, {
  icon: ShuffleIcon,
  title: 'Your students need more variety',
  text: 'Would you like to use more partner drills, musicality challenges, and group activities?'
}, {
  icon: LayoutGridIcon,
  title: 'You need a clearer structure',
  text: 'Would you like everything organised so you can quickly find what you need?'
}];

// Spine colours mirror the physical box (red, amber, green, blue, purple, navy).
export const volumes: Array<{
  number: string;
  title: string;
  description: string;
  heading?: string;
  intro?: string;
  items?: string[];
  outro?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volume 01',
  title: 'Salsa Foundations & Timing',
  description: 'Build confidence, introduce essential salsa skills, and help students find the beat together.',
  heading: 'Give Your Students a Confident Start in Salsa.',
  intro: 'The first volume focuses on salsa foundations, timing, and practical skills adult beginners can use from the very first class.',
  items: ['Introduction to salsa and its rhythm', 'Finding the beat and counting to the music', 'Basic step and footwork warm-ups', 'Weight transfer and balance exercises', 'Confidence-building group drills', 'Solo shines for beginners', 'Printable practice sheets', 'Partner and group exercises', 'Reflection and assessment'],
  outro: 'Ready-to-use material that makes salsa practical, accessible, and engaging.',
  spine: 'bg-red-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 02',
  title: 'Bachata Foundations & Connection',
  description: 'Develop the bachata basic, hip action, body movement, and comfortable connection through practical drills.',
  spine: 'bg-amber-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 03',
  title: 'Partnerwork & Lead-Follow',
  description: 'Help students build frame, clear leading and following, and respectful communication with any partner.',
  spine: 'bg-green-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 04',
  title: 'Musicality & Styling',
  description: 'Guide students to hear the music, dance on phrase, and add body movement and styling with confidence.',
  spine: 'bg-blue-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 05',
  title: 'Turn Patterns & Combinations',
  description: 'Introduce turns, spins, and partner combinations step by step, with clear progressions for mixed levels.',
  spine: 'bg-purple-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 06',
  title: 'Class Planning & Assessment',
  description: 'Bring learning together with yearly class plans, progress checks, and support for social dancing.',
  spine: 'bg-blue-900 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 Digital Volumes for a Full Year of Salsa & Bachata Teaching',
  text: 'A complete salsa & bachata bundle to help you plan and deliver classes throughout the year. Each volume contains 40 pages of teaching material, drills, and class activities.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Ready-to-teach class plans',
  text: 'Stop starting with a blank document. Clear class structures, instructions, and activities help you get prepared faster.'
}, {
  icon: FileTextIcon,
  title: 'Printable practice sheets',
  text: 'Resources for footwork, timing, partnerwork, planning, and reflection — ready to print and use.'
}, {
  icon: MusicIcon,
  title: 'Practical dance drills',
  text: 'Timing, footwork, connection, and styling activities help students put salsa and bachata skills into practice.'
}, {
  icon: DicesIcon,
  title: 'Class games and activities',
  text: 'Musicality games, partner challenges, and group exercises give students different ways to practise and participate.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Assessment tools',
  text: 'Progress checks, observation guides, and reflection prompts support feedback and help you follow students’ progress.'
}, {
  icon: CalendarCheckIcon,
  title: 'Yearly planning',
  text: 'Support for organising your salsa and bachata teaching and choosing topics to fit your class schedule.'
}];
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [{
  src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/16e101e2-d128-446d-88a2-fefcb60ddae2/1792291380782_sbr-v1-p2-8-week-progression-3757e22a.webp',
  label: 'Course Plan · Page 2',
  title: 'What You’ll Teach — 8-Week Progression',
  text: 'A clear 8-week roadmap from culture and beat to salsa, bachata and partner connection — with learning outcomes for every stage.'
}, {
  src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/16e101e2-d128-446d-88a2-fefcb60ddae2/1792291384712_sbr-v1-p13-bachata-basic-0e984125.webp',
  label: 'Ready-to-Teach Lesson · Page 13',
  title: 'Bachata Basic Step and Tap',
  text: 'A minute-by-minute lesson with warm-up, teach, practice, apply and reflect — plus reduced-step options.'
}, {
  src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/16e101e2-d128-446d-88a2-fefcb60ddae2/1792291388641_sbr-v1-p10-salsa-forward-back-5bcf887b.webp',
  label: 'Visual Practice Lab · Page 10',
  title: 'Salsa Forward & Back',
  text: 'Count-by-count footprint diagrams and common-error mini guides that make the salsa basic easy to see and teach.'
}, {
  src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/16e101e2-d128-446d-88a2-fefcb60ddae2/1792291392393_sbr-v1-p16-weight-transfer-9b1b3d2b.webp',
  label: 'Visual Practice Lab · Page 16',
  title: 'Weight Transfer',
  text: 'Step-by-step photo sequences, balance support and follow-the-leader drills for confident weight changes.'
}, {
  src: 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/16e101e2-d128-446d-88a2-fefcb60ddae2/1792291396374_sbr-v1-p37-skills-check-0b2a7202.webp',
  label: 'Assessment · Page 37',
  title: 'Volume 1 Skills Check',
  text: 'A visual progress snapshot for rhythm, balance, salsa and bachata basics — getting started, practicing, ready to build.'
}];
export const pageTypes = ['Ready-to-teach class plans', 'Printable practice sheets', 'Practical drills', 'Assessment resources'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Warm-Ups That Build Musicality',
  text: 'Printable warm-up drills that cue timing, rhythm awareness, and musical phrasing.',
  items: ['Timing and counting warm-ups', 'Rhythm awareness drills', 'Musical phrasing cues', 'Printable warm-up cards'],
  benefit: 'Students start dancing with the music, not against it.'
}, {
  title: 'Partnerwork Connection Drills',
  text: 'Step-by-step partner exercises for frame, lead-and-follow basics, timing, and communication.',
  items: ['Frame and connection drills', 'Lead-and-follow basics', 'Partner timing exercises', 'Communication practice'],
  benefit: 'Less confusion. More reliable technique.'
}, {
  title: 'Progress Check Templates',
  text: 'Simple tools to evaluate skills, spot gaps fast, and assign the right next practice.',
  items: ['Skill evaluation templates', 'Teacher observation sheets', 'Gap-spotting checklists', 'Next-practice planning'],
  benefit: 'Consistent week-to-week improvement.'
}, {
  title: 'Dance Class Energizers & Icebreakers',
  text: '30 quick activities to build confidence, connection, and fun in your classes.',
  items: ['Class openers', 'Mid-class energy resets', 'Closing activities', 'No-touch and solo options'],
  benefit: 'More variety without having to create every activity yourself.'
}, {
  title: 'Salsa & Bachata Practice Night – Teacher’s Social Dance Event Kit',
  text: 'Everything you need to host relaxed, inclusive practice nights for your students.',
  items: ['Event formats and timeline', 'Studio setup and floor map', 'Practice round cards', 'Printable studio signs'],
  benefit: 'Help students take their skills to the social floor.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Open SALSA & BACHATA READY™',
  text: 'Download the bundle and open the volume that matches your class topic.'
}, {
  title: 'Choose Your Class Plan and Materials',
  text: 'Find the teaching area and select the drills, practice sheets, or activities that suit your students.'
}, {
  title: 'Prepare in 5 Minutes',
  text: 'Review the instructions and select the materials and music you need.'
}, {
  title: 'Print and Teach',
  text: 'Print your practice sheets or use the resources digitally — and walk into class with a clear plan.'
}];
export const audienceItems = ['Teach salsa or bachata', 'Want a full year of salsa & bachata material organised', 'Want to stop creating every activity from scratch', 'Need practical timing, footwork, and partnerwork drills', 'Want to use more musicality games and partner activities', 'Need easy access to assessment and progress-check resources', 'Want to save time on weekly planning', 'Want to feel better prepared for your classes'];
export const timeValueTasks = ['Searching for drills and combinations', 'Writing counts and teaching cues', 'Creating footwork and practice sheets', 'Preparing partner drills and group activities', 'Building assessment and feedback resources'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digital volumes — 240 pages',
  detail: 'Salsa and bachata foundations, partnerwork, lead-follow, musicality, styling, turn patterns, and class planning'
}, {
  label: '5 bonuses at no extra cost',
  detail: 'Musicality warm-ups, partnerwork drills, progress checks, class energizers, and a practice night kit'
}, {
  label: 'A full year of salsa & bachata teaching support',
  detail: 'Ready-to-teach class plans, practical drills, and class activities'
}];
export const offerIncludes = ['Instant digital download', 'PDFs to save', 'Printable resources', 'No physical product shipped'];
export const PRICE_AMOUNT = '$19';
export const PRICE_CURRENCY = 'USD';
export const PRICE = `${PRICE_AMOUNT} ${PRICE_CURRENCY}`;

// The section stays hidden while empty. Only add real testimonials.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
  note?: string;
}> = [{
  quote: 'My Sunday evenings finally feel like mine again. Planning salsa and bachata classes used to take up so much of my weekend. Having the class structures and drills organised gives me a starting point I can adapt to my students, instead of creating everything from scratch.',
  name: 'Emma Collins',
  location: 'London, United Kingdom'
}, {
  quote: 'Knowing how to dance and explaining a movement to beginners are very different things. The timing and footwork exercises help me break things down and give my classes a clearer progression.',
  name: 'Daniel Mitchell',
  location: 'Austin, United States'
}, {
  quote: 'I was repeating the same activities because I didn’t have time to find new ones. The musicality exercises and partnerwork drills give me fresh options when I’m planning my next class.',
  name: 'Sophie Bennett',
  location: 'Melbourne, Australia'
}, {
  quote: 'I initially wanted the lesson plans, but the progress checks and class energizers have been useful too. It’s helpful to have resources for both teaching the class and deciding what students need to practise next.',
  name: 'Olivia Parker',
  location: 'Toronto, Canada'
}];
export const faqs = [{
  q: 'How do I receive the material?',
  a: 'SALSA & BACHATA READY™ is delivered digitally. After purchase, you receive access to PDF files you can download and save. Use the resources on your computer, tablet, or phone, and print the practice sheets you need.'
}, {
  q: 'Is SALSA & BACHATA READY™ in English?',
  a: 'Yes. The class plans, instructions, drills, and teaching resources are in English.'
}, {
  q: 'Is it suitable for my salsa and bachata classes?',
  a: 'The bundle covers salsa and bachata foundations through partnerwork, musicality, turn patterns, and class planning. It is designed for adult beginner and mixed-level classes — choose activities and adjust their complexity to match your students’ experience and goals.'
}, {
  q: 'Can I really prepare a class in 5 minutes?',
  a: 'The material is organised so you can choose a class plan, review the instructions, and select your resources in around five minutes. Activities requiring music selection, equipment, or a larger studio space may need additional setup.'
}, {
  q: 'Does the material cover a full year of classes?',
  a: 'The six volumes provide a structure for salsa and bachata teaching throughout the year. Use the planning resources to organise the material around your schedule, class length, and students’ progress.'
}, {
  q: 'Are ready-to-use practice sheets included?',
  a: 'Yes. The bundle includes printable resources for practical drills, footwork, partnerwork, planning, and reflection.'
}, {
  q: 'Are assessment resources included?',
  a: 'Yes. Assessment and reflection tools help you observe students’ dancing and give structured feedback.'
}, {
  q: 'Can I use it with students at different levels?',
  a: 'You can select drills and adjust group sizes, pattern complexity, practice time, and support to suit your students.'
}, {
  q: 'Do I have to print everything?',
  a: 'No. Review the material digitally and print only the pages you need.'
}, {
  q: 'Are the five bonuses included?',
  a: `Yes. All five bonuses are included in the ${PRICE} bundle at no extra cost.`
}, {
  q: 'Is this a subscription?',
  a: `No. It is a one-time payment of ${PRICE}, with no subscription.`
}, {
  q: 'Can I save the files?',
  a: 'Yes. Download and save the PDFs for your class preparation.'
}, {
  q: 'Can I use the material on my phone or tablet?',
  a: 'Yes. Open the PDF files on a compatible phone, tablet, or computer.'
}, {
  q: 'Is there a guarantee?',
  a: 'Yes. Your purchase includes a 7-day satisfaction guarantee, according to the guarantee terms.'
}];
